import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getRecaptchaToken } from '../utils/recaptcha';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { Google } from '@mui/icons-material';
// Removed Redux and old authService imports - now using AuthContext

const authTeal = '#185451';
const authTealDark = '#134e4a';

const authFieldSx = {
  '& label.Mui-focused': {
    color: authTeal,
  },
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: authTeal,
      boxShadow: '0 0 0 3px rgba(24, 84, 81, 0.14)',
    },
  },
};

const authOutlinedButtonSx = {
  color: authTeal,
  borderColor: 'rgba(24, 84, 81, 0.45)',
  '&:hover': {
    borderColor: authTeal,
    backgroundColor: 'rgba(24, 84, 81, 0.06)',
  },
};

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [googleError, setGoogleError] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithGoogle, isLoading, error, isAuthenticated, clearError } = useAuth();

  const fromState = location.state?.from;
  // ?next= (app-relative only) wins: links like "Sign in" from the guest
  // project wizard need to land back on the wizard, not the dashboard.
  // A ?next= left over from registration (kept in localStorage across the
  // verify-email step) also counts, so a guest who described their project
  // first still lands back on it if verification fell through to login.
  // Read once: the value is cleared on sign-in, and a re-render must not
  // recompute `from` as /dashboard before the navigation has happened.
  const [storedNext] = useState(() => { try { return localStorage.getItem('pending_verification_next'); } catch (_) { return null; } });
  const nextParam = new URLSearchParams(location.search || '').get('next') || storedNext;
  const from = (nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//'))
    ? nextParam
    : (fromState
      ? `${fromState.pathname || '/dashboard'}${fromState.search || ''}${fromState.hash || ''}`
      : '/dashboard');
  // `from` is a plain string, so the rest of the caller's state would be lost on
  // the way back. Carry the pieces the destination needs: a buyer who clicked
  // the heart on a listing expects it saved when they land back on /properties.
  const saveListingId = location.state?.saveListingId;
  const carryState = saveListingId ? { saveListingId } : undefined;

  useEffect(() => {
    if (isAuthenticated) {
      try { localStorage.removeItem('pending_verification_next'); } catch (_) { /* ignore */ }
      navigate(from, { replace: true, state: saveListingId ? { saveListingId } : undefined });
    }
  }, [isAuthenticated, navigate, from, saveListingId]);

  useEffect(() => {
    // Set email from location state (e.g., after registration)
    if (location.state?.email) {
      setFormData(prev => ({ ...prev, email: location.state.email }));
    }

    // Set success message from location state
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    }

    // Clear any stale errors on initial mount only
    clearError();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const validateField = (name, value) => {
    const errors = { ...validationErrors };
    
    switch (name) {
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value) {
          errors[name] = 'Email is required';
        } else if (!emailRegex.test(value)) {
          errors[name] = 'Please enter a valid email address';
        } else {
          delete errors[name];
        }
        break;
        
      case 'password':
        if (!value) {
          errors[name] = 'Password is required';
        } else if (value.length < 8) {
          errors[name] = 'Password must be at least 8 characters';
        } else {
          delete errors[name];
        }
        break;
        
      default:
        break;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const previousValue = formData[name];

    setFormData({
      ...formData,
      [name]: value
    });

    // Validate field on change
    validateField(name, value);

    // Clear global error only when user actually modifies the input
    // (not on initial focus or autofill)
    if (error && previousValue !== value && value.length > 0) {
      clearError();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const isEmailValid = validateField('email', formData.email);
    const isPasswordValid = validateField('password', formData.password);
    
    if (!isEmailValid || !isPasswordValid) {
      return;
    }
    
    // Get reCAPTCHA token (returns null if not configured)
    const recaptchaToken = await getRecaptchaToken('login');

    // Use AuthContext login method
    const result = await login(formData.email, formData.password, recaptchaToken);
    
    if (result.success) {
      // Navigation is handled by the useEffect above when isAuthenticated changes
    }
    // Error handling is managed by AuthContext
  };

  const handleGoogleLogin = async () => {
    setGoogleError('');
    try {
      // Load Google Identity Services library
      if (!window.google) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);

        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
          setTimeout(reject, 10000); // 10 second timeout
        });
      }

      // Initialize and show Google Sign-In
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: async (response) => {
          try {
            const result = await loginWithGoogle(response.credential);
            if (result.success) {
              navigate(from, { replace: true, state: carryState });
            } else {
              setGoogleError(result.error || 'Google sign-in failed. Please try again.');
            }
          } catch (err) {
            setGoogleError('Google sign-in failed. Please try again.');
          }
        }
      });

      // Prompt the user to sign in
      window.google.accounts.id.prompt();
    } catch (error) {
      setGoogleError('Google sign-in is not available. Please use email and password.');
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 6, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box textAlign="center" mb={3}>
          <Box
            component="img"
            src="/images/logo-eqaya-mark.png"
            alt="Eqaya"
            sx={{ height: 44, mb: 1.5, display: 'inline-block' }}
          />
          <Typography variant="h5" sx={{ fontFamily: "'Figtree', system-ui, sans-serif", fontWeight: 700, color: '#33373A' }} gutterBottom>
            Welcome back
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Sign in to your Eqaya account
          </Typography>
        </Box>

        {location.state?.notice && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {location.state.notice}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email Address"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            margin="normal"
            required
            autoComplete="email"
            autoFocus
            error={!!validationErrors.email}
            helperText={validationErrors.email}
            sx={authFieldSx}
            InputProps={{
              startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />
            }}
          />
          <TextField
            fullWidth
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            margin="normal"
            required
            autoComplete="current-password"
            error={!!validationErrors.password}
            helperText={validationErrors.password}
            sx={authFieldSx}
            InputProps={{
              startAdornment: <Lock sx={{ mr: 1, color: 'action.active' }} />,
              endAdornment: (
                <Button
                  onClick={() => setShowPassword(!showPassword)}
                  size="small"
                  sx={{ color: authTeal }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </Button>
              )
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
              <Typography variant="body2" sx={{ color: authTeal, '&:hover': { textDecoration: 'underline' } }}>
                Forgot password?
              </Typography>
            </Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{
              mt: 1,
              mb: 2,
              backgroundColor: authTeal,
              '&:hover': { backgroundColor: authTealDark },
            }}
            disabled={isLoading || Object.keys(validationErrors).length > 0}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                Signing In...
              </Box>
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            OR
          </Typography>
        </Divider>

        {/* Google OAuth Login */}
        <Button
          fullWidth
          variant="outlined"
          size="large"
          sx={{ mb: googleError ? 1 : 2, ...authOutlinedButtonSx }}
          onClick={handleGoogleLogin}
          disabled={isLoading}
          startIcon={<Google />}
        >
          Continue with Google
        </Button>

        {googleError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGoogleError('')}>
            {googleError}
          </Alert>
        )}

        <Box textAlign="center">
          <Typography variant="body2" sx={{ mb: 2 }}>
            Don't have an account?
          </Typography>
          <Button
            component={Link}
            to={`/register${location.search}`}
            state={location.state}
            variant="outlined"
            fullWidth
            sx={authOutlinedButtonSx}
          >
            Create Account
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;
